import React from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import CloseIcon from '@material-ui/icons/Close'
import InfoIcon from '@material-ui/icons/Info'
import WarningIcon from '@material-ui/icons/Warning'
import { theme } from '../../config/muiThemeConfig'
import { getSession } from '../../utils/api'

const SUCCESS_COLOR = '#41C396'
const DANGER_COLOR = '#DB572E'
const TABLE_COLUMN_WIDTHS = {
  field: '34%',
  current: '33%',
  next: '33%',
}

const toComparableNumber = (value) => {
  if (value === null || value === undefined) return null
  const normalized = `${value}`.replace(/,/g, '').trim()
  const parsed = Number(normalized.replace(/[^0-9.-]/g, ''))
  return Number.isNaN(parsed) ? null : parsed
}

const getNumericTrend = (before, after, label = '') => {
  if (ALWAYS_PRIMARY_FIELDS.has(label)) return null
  const beforeNumber = toComparableNumber(before)
  const afterNumber = toComparableNumber(after)
  if (beforeNumber === null || afterNumber === null) return null
  if (afterNumber > beforeNumber) return 'up'
  if (afterNumber < beforeNumber) return 'down'
  return null
}

const ALWAYS_PRIMARY_FIELDS = new Set(['Tax Display Name'])

const getNewValueColor = (before, after, label = '') => {
  if (ALWAYS_PRIMARY_FIELDS.has(label)) {
    return theme.palette.primary.main
  }

  const beforeText = `${before || ''}`.toLowerCase()
  const afterText = `${after || ''}`.toLowerCase()

  if (
    (afterText === 'enabled' || afterText === 'yes') &&
    (beforeText === 'disabled' || beforeText === 'no')
  ) {
    return SUCCESS_COLOR
  }

  if (
    (afterText === 'disabled' || afterText === 'no') &&
    (beforeText === 'enabled' || beforeText === 'yes')
  ) {
    return DANGER_COLOR
  }

  const beforeNumber = toComparableNumber(before)
  const afterNumber = toComparableNumber(after)
  if (beforeNumber !== null && afterNumber !== null) {
    if (afterNumber > beforeNumber) return SUCCESS_COLOR
    if (afterNumber < beforeNumber) return DANGER_COLOR
  }

  return theme.palette.primary.main
}

const getTerritoryGrouping = (change = {}) => {
  const metadataTerritory = change.metadata && change.metadata.territory
  const metadataFieldLabel = change.metadata && change.metadata.fieldLabel
  if (metadataTerritory) {
    return {
      territory: metadataTerritory,
      fieldLabel: metadataFieldLabel || change.label,
    }
  }
  return null
}

const renderNewValue = (change) => {
  const trend = getNumericTrend(change.before, change.after, change.label)
  return (
    <TableCell
      style={{
        color: getNewValueColor(change.before, change.after, change.label),
        fontWeight: 600,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <span>{change.after}</span>
        {trend === 'up' ? <ArrowUpwardIcon style={{ fontSize: '20px', fontWeight: 700 }} /> : null}
        {trend === 'down' ? (
          <ArrowDownwardIcon style={{ fontSize: '20px', fontWeight: 700 }} />
        ) : null}
      </span>
    </TableCell>
  )
}

const ChangeTable = ({ rows = [], fixedLayout = false, columnWidths = {} }) => (
  <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
    <Table size="small" style={fixedLayout ? { tableLayout: 'fixed', width: '100%' } : undefined}>
      <TableHead>
        <TableRow style={{ backgroundColor: '#fafafa' }}>
          <TableCell
            style={{
              fontWeight: 700,
              textTransform: 'uppercase',
              ...(fixedLayout ? { width: columnWidths.field } : {}),
            }}
          >
            Field
          </TableCell>
          <TableCell
            style={{
              fontWeight: 700,
              textTransform: 'uppercase',
              ...(fixedLayout ? { width: columnWidths.current } : {}),
            }}
          >
            Current Value
          </TableCell>
          <TableCell
            style={{
              fontWeight: 700,
              textTransform: 'uppercase',
              ...(fixedLayout ? { width: columnWidths.next } : {}),
            }}
          >
            New Value
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((change, index) => (
          <TableRow key={`${change.label}-${index}`}>
            <TableCell>{change.label}</TableCell>
            <TableCell>{change.before}</TableCell>
            {renderNewValue(change)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
)

const NoteSection = () => (
  <div
    style={{
      border: `1px solid ${theme.palette.primary.light}`,
      borderRadius: '4px',
      padding: '12px',
      backgroundColor: '#f5f7fa',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}
  >
    <InfoIcon style={{ color: theme.palette.primary.main, fontSize: '18px' }} />
    <Typography style={{ fontSize: '14px' }}>
      <strong>Note:</strong> Upon confirmation, an automated alert will be sent to all Admins for
      this location.
    </Typography>
  </div>
)

const TaxChangesConfirmationModal = ({
  open = false,
  title = 'Confirm Changes',
  changes = [],
  groupByTerritory = false,
  onClose,
  onConfirm,
}) => {
  const session = getSession() || {}
  const shopName = session.assignedShop && session.assignedShop.name
  const titleWithShop = shopName
    ? `Critical Changes for ${shopName}: ${title}`
    : `Critical Changes: ${title}`

  const groupedTerritoryChanges = changes.reduce(
    (acc, change) => {
      const parsedLabel = getTerritoryGrouping(change)
      if (!parsedLabel) {
        acc.ungrouped.push(change)
        return acc
      }

      const { territory, fieldLabel } = parsedLabel
      acc.groups[territory] = [...(acc.groups[territory] || []), { ...change, label: fieldLabel }]
      return acc
    },
    { groups: {}, ungrouped: [] },
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { overflow: 'hidden' } }}
    >
      <div
        style={{
          backgroundColor: theme.palette.primary.main,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '18px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon style={{ color: '#fff' }} />
          </div>
          <Typography variant="h6" style={{ color: '#fff', fontWeight: 600 }}>
            {titleWithShop}
          </Typography>
        </div>
        <IconButton onClick={onClose} style={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '55vh' }}>
        <DialogContent style={{ padding: '20px', overflowY: 'auto', flex: '1 1 auto' }}>
          <Typography style={{ marginBottom: '12px' }}>
            You are making a manual override to the current tax structure. Incorrect settings may
            lead to <strong>under-collection</strong> or <strong>over-collection</strong> of
            revenue. Please review the changes below before proceeding.
          </Typography>
          {changes.length ? (
            groupByTerritory ? (
              <div>
                {Object.keys(groupedTerritoryChanges.groups).map((territory) => (
                  <div
                    key={territory}
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      marginBottom: '12px',
                    }}
                  >
                    <div
                      style={{ backgroundColor: '#fafafa', padding: '10px 12px', fontWeight: 700 }}
                    >
                      {territory} Tax
                    </div>
                    <ChangeTable
                      rows={groupedTerritoryChanges.groups[territory]}
                      fixedLayout={true}
                      columnWidths={TABLE_COLUMN_WIDTHS}
                    />
                  </div>
                ))}
                {groupedTerritoryChanges.ungrouped.length ? (
                  <ChangeTable
                    rows={groupedTerritoryChanges.ungrouped}
                    fixedLayout={true}
                    columnWidths={TABLE_COLUMN_WIDTHS}
                  />
                ) : null}
              </div>
            ) : (
              <ChangeTable rows={changes} />
            )
          ) : (
            <Typography>No changes found.</Typography>
          )}
        </DialogContent>
        <div style={{ flexShrink: 0, borderTop: '1px solid #e0e0e0', padding: '12px 20px 8px' }}>
          <NoteSection />
        </div>
      </div>
      <DialogActions style={{ padding: '0 20px 20px', gap: '8px' }}>
        <Button onClick={onClose} className="button-white-and-red-padded">
          Cancel
        </Button>
        <Button onClick={onConfirm} className="button-blue-and-white-padded" autoFocus>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TaxChangesConfirmationModal



// WEBPACK FOOTER //
// ./src/components/TaxOption/TaxChangesConfirmationModal.js